import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";

import Modal from "../Modal";
import AvatarWithOnlineStatus from "../AvatarWithOnlineStatus";
import VerifiedUserIcon from "../VerifiedUserIcon";
import FollowerIcon from "../FollowerIcon";
import PromoterIcon from "../PromoterIcon";
import SendTipForm from "./components/SendTipForm";
import { Button } from "../Button";
import SelectCardForm from "./components/SelectCardForm";
import Loader from "../Loader";
import { useDefaultCard } from "@hooks/useDefaultCard";
import { generateUniqueID } from "@helpers/uniqID";
import StorageService from "../../services/StorageService";
import UsersService from "../../services/UsersService";
import ToasterService from "../../services/ToastService";
import { moneyPipe, toCents } from "@helpers/price";
import { USD_DECIMAL_PART } from "@validation/lengthConstants";
import { removeEmptyKeys } from "@helpers/common";
import { handleThreeDC } from "@helpers/payments";
import { localstorageKeys } from "@const/store";
import {
  sendDonation,
  sendDonationFailure,
  sendDonationSuccess,
  setDefaultCard,
} from "@redux/payments/actions";
import {
  initialValues,
  selectCardFormValidationSchema,
  sendTipValidationSchema,
} from "./form";
import { sendMessageSuccess } from "@redux/conversations/actions";
import { getCards } from "@redux/paymentCards/actions";

import styles from "./index.module.scss";
import routes from "../../const/routes";

const steps = {
  0: SendTipForm,
  1: SelectCardForm,
};

const validations = {
  0: sendTipValidationSchema,
  1: selectCardFormValidationSchema,
};

const formatter = moneyPipe(USD_DECIMAL_PART);

const defaultCurrentStep = 0;

const SendTipModal = ({
  isOpen,
  toggle,
  creatorId,
  isSupporter,
  isMostProfitableSupporter,
  isPromoter,
  isMostProfitablePromoter,
  conversationId,
  postId,
}) => {
  const [currentStep, setCurrentStep] = useState(defaultCurrentStep);
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);

  const [cardData, setCardData] = useState({});

  const { isMessageSending } = useSelector(
    ({ conversationsReducer }) => conversationsReducer,
  );
  const { cardsById } = useSelector(({ cardsReducer }) => cardsReducer);

  const trackingCode = StorageService.get(localstorageKeys.trackingCode, "");

  const defaultCard = useDefaultCard();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getCards());
    if (!defaultCard) return;
    formik.setFieldValue("cardId", defaultCard);
  }, [isOpen, defaultCard]);

  const opponent = useSelector(
    ({ authorsReducer }) => authorsReducer[creatorId],
  );

  const sendTipModalRef = useRef(null);

  const handleSendTip = ({ amount, cardId, threeDSecure, message }) => {
    const uuid = generateUniqueID();
    dispatch(sendDonation());

    handleFiatPayment({ amount, cardId, threeDSecure, uuid, message });
  };

  const handleFiatPayment = async ({
    amount,
    cardId,
    threeDSecure,
    uuid,
    message,
  }) => {
    try {
      const paymentAmount = toCents(amount).toFixed(0);

      const { data } = await UsersService.sendDonationToUserById(
        opponent.id,
        removeEmptyKeys({
          amount: paymentAmount,
          cardId,
          threeDSecure,
          trackingCode,
          messageUuid: uuid,
          message,
        }),
      );
      if (cardId !== defaultCard) dispatch(setDefaultCard({ id: cardId }));

      if (data?.is3DSRequired) {
        const card = cardsById[cardId];
        await handleThreeDC({
          setCardData,
          paymentTokenId: card.cardId,
          purchasePayment: handleFiatPayment,
          cardNumber: data?.card?.cardNumber,
          expMonth: data.card.expMonth,
          expYear: data.card.expYear,
          fullName: data?.card?.fullName,
          email: data?.card?.email,
          purchasePaymentParams: { amount, cardId, uuid, message },
          formRef: sendTipModalRef,
          handleCancelSecure: () => {
            dispatch(sendDonationFailure());
          },
        });
      } else {
        dispatch(
          sendMessageSuccess({
            message: data.message,
            conversationId,
            uuid,
          }),
        );
        dispatch(sendDonationSuccess());
        ToasterService.success(t2("PAYMENT_COMPLETED"));
        handleOnClose();
      }
    } catch (error) {
      ToasterService.error(error.generalMessage);
      dispatch(sendDonationFailure());
    }
  };

  const {
    name,
    avatar,
    username,
    isBlockedByYou,
    isBlockedByUser,
    isDeleted,
    isIdentityVerified,
  } = useSelector(({ authorsReducer }) => authorsReducer[creatorId]) || {};
  const { t } = useTranslation("modals");
  const { t: t1 } = useTranslation("buttons");
  const { t: t2 } = useTranslation("payment-completed");
  const handleOnClose = () => {
    toggle();
    setCurrentStep(defaultCurrentStep);
    formik.resetForm({ values: dynamicInitialValues });
  };

  const dynamicInitialValues = { ...initialValues, cardId: defaultCard };

  const handleOnConfirm = async () => {
    void formik.setTouched(true);
    const { amount, cardId, message } = formik.values;

    if (currentStep === 0) {
      if (formik.isValid) {
        setCurrentStep((step) => step + 1);
      }
    } else {
      handleSendTip({
        amount,
        cardId,
        message,
      });
      formik.setFieldValue("cardId", cardId);
    }
  };

  const formik = useFormik({
    initialValues: { ...dynamicInitialValues },
    validationSchema: validations[currentStep],
    onSubmit: handleOnConfirm,
  });

  useEffect(() => {
    if (defaultCard && formik.values.cardId === undefined) {
      formik?.setFieldValue("cardId", defaultCard);
    }
  }, [defaultCard, formik]);

  const FormContent = steps[currentStep];

  const submitButtonText =
    currentStep === 1
      ? t1("PAY_WITH_AMOUNT", {
          amount: formatter(formik.values.amount),
        })
      : t("SEND_TIP");

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleOnClose}
      onClose={handleOnClose}
      title={t("SEND_TIP")}
      bodyClassName="pt-3 pb-4"
      showCancel={false}
      showConfirm={false}
      footerClassName="d-none"
      confirmClassName="w-100 mw-100"
      unmountOnClose={true}
    >
      <div>
        <div
          className={classNames(
            "d-flex w-100 align-items-center mb-2",
            styles.container,
          )}
        >
          <AvatarWithOnlineStatus
            isDeleted={isDeleted}
            onClick={() => {}}
            avatar={avatar}
            className={styles.avatar}
            isBlockedByYou={isBlockedByYou}
            isBlockedByUser={isBlockedByUser}
          />

          <div className={classNames("me-auto ms-2 w-100")}>
            <div className="mb-1 mb-xl-0 d-flex align-items-center w-100">
              <div className="w-100">
                <div className="d-flex align-items-center">
                  <Link
                    to={`${routes.CREATOR_PROFILE()}${username}`}
                    className={classNames(
                      "d-block text-decoration-none overflow-hidden",
                      isDeleted && "pe-none",
                    )}
                  >
                    <div
                      className={classNames(
                        "notranslate text-body-2 text-body-1-xl text-decoration-none text-primary text-semibold text-truncate",
                        isDeleted && "mt-2 pt-1",
                      )}
                    >
                      {username}
                    </div>
                  </Link>

                  <VerifiedUserIcon
                    isVisible={isIdentityVerified}
                    className="ms-2"
                  />
                  <FollowerIcon
                    isSupporter
                    isFirst={isMostProfitableSupporter}
                    isPaid={isSupporter}
                  />
                  {isPromoter && (
                    <PromoterIcon
                      isFirst={isMostProfitablePromoter}
                      isNotOnlyBadge={isSupporter}
                    />
                  )}
                  <FollowerIcon isNotOnlyBadge={isSupporter || isPromoter} />
                </div>

                <Link
                  to={`${routes.CREATOR_PROFILE()}${username}`}
                  className={classNames(
                    "d-block text-decoration-none overflow-hidden",
                    isDeleted && "pe-none",
                  )}
                >
                  <div
                    className={classNames(
                      "text-body-2 text-decoration-none text-secondary text-truncate",
                      isDeleted && "mt-2 pt-1",
                      styles.paddingRight45,
                    )}
                  >
                    {name}
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <FormikProvider value={formik}>
          <form
            ref={sendTipModalRef}
            id="sendCardModalId"
            onSubmit={formik.handleSubmit}
            className="pt-2"
          >
            <FormContent
              values={formik.values}
              isMessageSending={isMessageSending}
              username={username}
              setIsAddCardOpen={setIsAddCardOpen}
              isAddCardOpen={isAddCardOpen}
              message={formik.values.message}
              amount={toCents(formik.values.amount)}
              postId={postId}
              userId={opponent?.id}
              cardId={formik.values.cardId}
            />
            <Button
              type="submit"
              color="primary"
              className="w-100"
              disabled={!formik.isValid || isMessageSending}
            >
              {isMessageSending ? <Loader /> : submitButtonText}
            </Button>

            {isOpen && !isAddCardOpen && (
              <div className="d-none">
                <input
                  type="text"
                  name="currencyCode"
                  placeholder="currencyCode"
                  data-ccbill="currencyCode"
                  value="840"
                />
                <input
                  type="number"
                  name="amount"
                  placeholder="amount"
                  data-ccbill="amount"
                  value={formik?.values?.amount}
                />
                <input
                  type="text"
                  name="cardNumber"
                  placeholder="cardNumber"
                  data-ccbill="cardNumber"
                  value={cardData?.cardNumber}
                />
                <input
                  type="text"
                  name="expMonth"
                  placeholder="expMonth"
                  data-ccbill="expMonth"
                  value={cardData?.expMonth}
                />
                <input
                  type="string"
                  name="ipAddress"
                  placeholder="ipAddress"
                  data-ccbill="ipAddress"
                  value={cardData?.ipAddress}
                />
                <input
                  type="string"
                  name="email"
                  placeholder="email"
                  data-ccbill="email"
                  value={cardData?.email}
                />
                <input
                  type="text"
                  name="nameOnCard"
                  placeholder="Name on Card"
                  data-ccbill="nameOnCard"
                  value={cardData?.fullName}
                />
                <input
                  type="text"
                  name="expYear"
                  placeholder="expYear"
                  data-ccbill="expYear"
                  value={cardData?.expYear}
                />
                <input
                  type="string"
                  name="email"
                  placeholder="email"
                  data-ccbill="email"
                  value={cardData?.email}
                />
                <input
                  type="text"
                  name="nameOnCard"
                  placeholder="Name on Card"
                  data-ccbill="nameOnCard"
                  value={cardData?.fullName}
                />
              </div>
            )}
          </form>
        </FormikProvider>
      </div>
    </Modal>
  );
};

export default SendTipModal;
